import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Grid, Stack, TextField, Typography } from '@mui/material';
import {
  CuriosityAutocomplete,
  CuriosityDisplayContextEnum,
  CuriosityNamespaceType,
  CuriosityRecommendationBox,
} from '@procureai/curiosity-ui-mui';
import { NumericFormatDecimal, useCurrency } from '@procureai/ui-mui';
import Decimal from 'decimal.js';
import { useEffect, useMemo } from 'react';
import { Controller, ControllerFieldState, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  IntakeRequestLineItem,
  intakeRequestLineItemSchema,
  lineItemDefault,
} from '../../IntakeRequestLineItemStep.schema';
import { DateRangePicker } from '../DateRangePicker/DateRangePicker';
import { IntakeRequestStepLineItemTotal } from '../IntakeRequestStepLineItemTotal/IntakeRequestStepLineItemTotal';
import { I18nNamespace } from '~/common/types';
import { FormChangeDebouncer } from '~/routes/IntakeDetail/components/FormChangeDebouncer/FormChangeDebouncer';

export type IntakeRequestStepLineItemFormProps = {
  disabled?: boolean;
  lineItem?: IntakeRequestLineItem;
  onChange: (lineItem: IntakeRequestLineItem) => void;
  validate?: boolean;
};

export const IntakeRequestStepLineItemForm = ({
  disabled,
  lineItem,
  validate,
  onChange,
}: IntakeRequestStepLineItemFormProps) => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeRequestLineItemStepForm' });

  const form = useForm({
    defaultValues: lineItemDefault as IntakeRequestLineItem,
    resolver: zodResolver(intakeRequestLineItemSchema),
    mode: 'all',
  });

  useEffect(() => {
    form.reset(lineItem);
    if (validate) {
      form.trigger();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const commonTextFieldProps = (fieldState: ControllerFieldState) => ({
    variant: 'outlined' as const,
    fullWidth: true,
    margin: 'normal' as const,
    error: !!fieldState.error,
    helperText: fieldState.error?.message,
  });

  const { symbol } = useCurrency();
  const NumberInputComponent = useMemo(() => NumericFormatDecimal({ decimalScale: 2 }), []);

  return (
    <>
      <FormChangeDebouncer control={form.control} onChange={onChange} />
      <Grid
        container
        direction="column"
        spacing={4}
        sx={{
          justifyContent: 'flex-start',
        }}
      >
        <Grid size={12}>
          <Controller
            control={form.control}
            name="position"
            render={({ field, fieldState }) => {
              return (
                <TextField
                  {...field}
                  {...commonTextFieldProps(fieldState)}
                  value={field.value || ''}
                  disabled={disabled}
                  label={t('position')}
                  required
                />
              );
            }}
          />
          <Controller
            control={form.control}
            name="title"
            render={({ field, fieldState }) => {
              return (
                <TextField
                  {...field}
                  {...commonTextFieldProps(fieldState)}
                  disabled={disabled}
                  label={t('title')}
                  required
                />
              );
            }}
          />
          <Controller
            control={form.control}
            name="commodityGroupId"
            render={({ field, fieldState }) => {
              return (
                <CuriosityAutocomplete
                  {...field}
                  disabled={disabled}
                  fullWidth
                  ns={CuriosityNamespaceType.COMMODITY_GROUP}
                  simplifyValue
                  TextFieldProps={{
                    variant: 'outlined',
                    sx: { marginTop: '16px', marginBottom: '8px' },
                    label: t('commodityGroup'),
                    error: !!fieldState.error,
                    required: true,
                  }}
                />
              );
            }}
          />
          <Controller
            control={form.control}
            name="description"
            render={({ field, fieldState }) => {
              return (
                <TextField
                  {...field}
                  {...commonTextFieldProps(fieldState)}
                  multiline
                  maxRows={10}
                  disabled={disabled}
                  label={t('description')}
                />
              );
            }}
          />
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: 'flex-start',
            }}
          >
            <Box
              sx={{
                flex: 0.25,
              }}
            >
              <Controller
                control={form.control}
                name="quantity"
                render={({ field, fieldState }) => {
                  return (
                    <TextField
                      {...field}
                      {...commonTextFieldProps(fieldState)}
                      disabled={disabled}
                      label={t('quantity')}
                      value={!field.value ? 0 : new Decimal(field.value).toNumber()}
                      onChange={(event) => {
                        const output = parseFloat(event.target.value);
                        const val = isNaN(output) ? 0 : output;
                        field.onChange(val);
                      }}
                      required
                      slotProps={{
                        input: {
                          inputComponent: NumberInputComponent as any,
                        },
                      }}
                    />
                  );
                }}
              />
            </Box>
            <Box
              sx={{
                flex: 0.75,
              }}
            >
              <Controller
                control={form.control}
                name="unitId"
                render={({ field, fieldState }) => {
                  return (
                    <CuriosityAutocomplete
                      {...field}
                      disabled={disabled}
                      ns={CuriosityNamespaceType.UNIT}
                      simplifyValue
                      fullWidth
                      TextFieldProps={{
                        label: t('unit'),
                        sx: { marginTop: '16px', marginBottom: '8px' },
                        required: true,
                        error: !!fieldState.error,
                      }}
                    />
                  );
                }}
              />
            </Box>
          </Stack>
          <CuriosityRecommendationBox
            ns={CuriosityNamespaceType.UNIT}
            displayContext={CuriosityDisplayContextEnum.FAVORITES}
            limitTags={3}
            onTagClick={({ key }) => {
              form.setValue('unitId', key);
            }}
          />
          <Controller
            control={form.control}
            name="expectedPricePerUnit"
            render={({ field, fieldState }) => {
              return (
                <TextField
                  {...field}
                  {...commonTextFieldProps(fieldState)}
                  value={!field.value ? 0 : new Decimal(field.value).toNumber()}
                  disabled={disabled}
                  label={t('expectedPricePerUnit')}
                  onChange={(event) => {
                    const output = parseFloat(event.target.value);
                    const val = isNaN(output) ? 0 : output;
                    field.onChange(val);
                  }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <Typography
                          color="textSecondary"
                          sx={{
                            paddingRight: 1,
                          }}
                        >
                          {symbol}
                        </Typography>
                      ),
                      inputComponent: NumberInputComponent as any,
                    },
                  }}
                />
              );
            }}
          />
          <DateRangePicker
            control={form.control}
            disabled={disabled}
            nameEarly="earlyDeliveryAt"
            nameLate="lateDeliveryAt"
            labelEarly={t('earliestDeliveryDate')}
            labelLate={t('latestDeliveryDate')}
          />
          <IntakeRequestStepLineItemTotal watch={form.watch} />
        </Grid>
      </Grid>
    </>
  );
};
